import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { tokPx } from '@sunwebgroup/ui-components'
import { Typography } from '@sunwebgroup/ui-components/uds'
import { isEmpty } from 'ramda'
import { styled } from 'styled-components'
import { HeadingLabel } from '../HeadingLabel'
import { RoomType } from './RoomType'
import ShowAllRoomTypes from './ShowAllRoomTypes'

const MAXIMUM_ROOM_TYPES_SHOWN = 4

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const RoomTypeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokPx('primitives_space_8')};
  margin-bottom: ${tokPx('primitives_space_12')};
`

const AdditionalInfo = styled.div`
  margin-top: ${tokPx('primitives_space_16')};
  & ul {
    padding: 0;
    margin-left: ${tokPx('primitives_space_24')};
    margin-top: 0;
    margin-bottom: 0;
  }
  min-width: ${tokPx('primitives_area_size_240')};
  max-width: ${tokPx('primitives_area_size_720')};
`

export function RoomTypes() {
  const roomTypes = useCMSPageField('PDP.roomTypes')
  const additionalInfoHtml = useCMSPageField('PDP.roomTypes.additionalInfoHtml')
  const intl = useIntl()
  const { sendEvent } = useAnalytics()

  const handleShowAllRoomTypes = (isOpen: boolean) => {
    if (isOpen) {
      sendEvent(createPDPClickEvent({
        element: 'Accordion',
        description: 'Show more room types',
        result: `Show all ${roomTypes.length} roomtypes`,
      }))
    }
    else {
      sendEvent(createPDPClickEvent({
        element: 'Accordion',
        description: 'Show less room types',
        result: `Show less roomtypes`,
      }))
    }
  }

  return (
    <Wrapper>
      <HeadingLabel text={intl.formatMessage({ id: 'roomtypes_heading' })} />
      <RoomTypeWrapper>
        {!isEmpty(roomTypes) && (
          roomTypes.slice(0, MAXIMUM_ROOM_TYPES_SHOWN).map(roomType => (
            <RoomType roomType={roomType} key={roomType.id} />
          ))
        )}
      </RoomTypeWrapper>
      {roomTypes.length > MAXIMUM_ROOM_TYPES_SHOWN && <ShowAllRoomTypes onOpenChange={handleShowAllRoomTypes} />}
      {additionalInfoHtml && (
        <AdditionalInfo>
          <Typography $variant="body-m-bold-tight">
            {intl.formatMessage({ id: 'roomtypes_extra_info_label' })}
          </Typography>
          <Typography $variant="body-m-regular-tight" dangerouslySetInnerHTML={{ __html: additionalInfoHtml }} />
        </AdditionalInfo>
      )}
    </Wrapper>
  )
}
